import type { RigGeometry } from '../BipedRig';

/**
 * Visual skins for the cutout rig. A skin is *data*: colours + body proportions.
 * The same AthleteRig code animates every skin — to add a species you add a SkinDef,
 * not new animation code. Placeholder shapes are generated in-engine (free); when the
 * Higgsfield art lands, each part swaps to an atlas frame at the same pivot.
 */

export type EarStyle = 'pointy' | 'round' | 'floppy';
export type TailStyle = 'long' | 'short' | 'puff' | 'none';

export interface SkinDef {
  id: string;
  /** Main body fill. */
  body: number;
  /** Belly / muzzle highlight. */
  belly: number;
  /** Limb colour (often a touch darker than body). */
  limb: number;
  /** Ear / nose accent. */
  accent: number;
  /** Spot / marking colour (placeholder uses it sparingly). */
  marking: number;
  // --- proportions (multipliers around a generic quadruped baseline) ---
  bodyLen: number;
  bodyHeight: number;
  legLen: number;
  legThick: number;
  headSize: number;
  neck: number;
  earStyle: EarStyle;
  tail: TailStyle;
  /** Animation personality: how bouncy/exaggerated the run reads. */
  bounce: number;
  /** Eye size multiplier — big eyes read cuter/funnier. */
  eye: number;
  /**
   * Optional whole-body Higgsfield sprite. When present (and loaded), the athlete is
   * drawn as a MascotSprite instead of the procedural rig. `key` is the texture key
   * loaded in Preload; `displayHeight` is the on-screen height in px.
   */
  sprite?: { key: string; displayHeight: number };
  /**
   * Optional articulated biped rig: cut part texture keys + target standing height.
   * Takes priority over `sprite` when its parts are loaded (real run cycle).
   */
  rig?: {
    parts: { head: string; torso: string; arm: string; leg: string; tail?: string };
    displayHeight: number;
    /** Per-animal pivot/rest overrides (parts are drawn differently per species). */
    geometry?: Partial<RigGeometry>;
  };
}

export const SKINS: Record<string, SkinDef> = {
  cheetah: {
    id: 'cheetah',
    body: 0xf2b441,
    belly: 0xfde8b8,
    limb: 0xe0a233,
    accent: 0x2a2118,
    marking: 0x3a2c16,
    bodyLen: 1.18,
    bodyHeight: 0.82,
    legLen: 1.18,
    legThick: 0.82,
    headSize: 0.9,
    neck: 1.0,
    earStyle: 'round',
    tail: 'long',
    bounce: 1.0,
    eye: 1.0,
    sprite: { key: 'cheetah_sprite', displayHeight: 300 },
    rig: {
      parts: {
        head: 'cheetah_part_head',
        torso: 'cheetah_part_torso',
        arm: 'cheetah_part_arm',
        leg: 'cheetah_part_leg',
        tail: 'cheetah_part_tail',
      },
      displayHeight: 300,
    },
  },
  sloth: {
    id: 'sloth',
    body: 0x9a8c6e,
    belly: 0xcabf9f,
    limb: 0x7d7058,
    accent: 0x4a3f2e,
    marking: 0x5c5340,
    bodyLen: 0.92,
    bodyHeight: 1.05,
    legLen: 0.86,
    legThick: 1.15,
    headSize: 1.12,
    neck: 0.7,
    earStyle: 'round',
    tail: 'short',
    bounce: 0.65,
    eye: 1.35,
    sprite: { key: 'sloth_sprite', displayHeight: 285 },
    rig: {
      parts: { head: 'sloth_part_head', torso: 'sloth_part_torso', arm: 'sloth_part_arm', leg: 'sloth_part_leg' },
      displayHeight: 290,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.5, 0.08], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 0, leg: 0, tail: 0, head: 0.18, torso: 0 },
        joints: { neck: [2, -0.6], shoulder: [0, -0.58], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  gorilla: {
    id: 'gorilla',
    body: 0x4a4a52,
    belly: 0x6b6b73,
    limb: 0x3c3c44,
    accent: 0x2a2a30,
    marking: 0x33333a,
    bodyLen: 1.3,
    bodyHeight: 1.2,
    legLen: 0.85,
    legThick: 1.4,
    headSize: 1.0,
    neck: 0.6,
    earStyle: 'round',
    tail: 'none',
    bounce: 0.85,
    eye: 0.9,
    sprite: { key: 'gorilla_sprite', displayHeight: 300 },
    rig: {
      parts: { head: 'gorilla_part_head', torso: 'gorilla_part_torso', arm: 'gorilla_part_arm', leg: 'gorilla_part_leg' },
      displayHeight: 300,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.22, 0.16], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 0.95, leg: 0, tail: 0, head: 0, torso: 0 },
        joints: { neck: [4, -0.82], shoulder: [13, -0.6], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  meerkat: {
    id: 'meerkat',
    body: 0xc99a63,
    belly: 0xe8d5b0,
    limb: 0xbf8f57,
    accent: 0x4a3526,
    marking: 0x6b4a30,
    bodyLen: 0.9,
    bodyHeight: 0.95,
    legLen: 1.0,
    legThick: 0.7,
    headSize: 1.0,
    neck: 0.8,
    earStyle: 'round',
    tail: 'long',
    bounce: 0.9,
    eye: 1.2,
    rig: {
      parts: {
        head: 'meerkat_part_head',
        torso: 'meerkat_part_torso',
        arm: 'meerkat_part_arm',
        leg: 'meerkat_part_leg',
        tail: 'meerkat_part_tail',
      },
      displayHeight: 300,
      // Meerkat limbs are drawn vertical (vs the cheetah's diagonal arm) and its tail
      // is horizontal — so its pivots/rest angles differ from the default.
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.5, 0.08], leg: [0.5, 0.05], tail: [0.95, 0.4] },
        rest: { arm: 0, leg: 0, tail: 0, head: 0, torso: 0 },
        // Tail attaches low at the base of the spine and trails behind (was too high).
        joints: { neck: [10, -0.9], shoulder: [-6, -0.74], hip: [0, -0.04], tail: [-45, -0.1] },
      },
    },
  },
  capybara: {
    id: 'capybara',
    body: 0x9c5f33,
    belly: 0xb87a4a,
    limb: 0x854f2a,
    accent: 0x4a2c16,
    marking: 0x6e4222,
    bodyLen: 1.15,
    bodyHeight: 1.05,
    legLen: 0.8,
    legThick: 1.2,
    headSize: 1.05,
    neck: 0.5,
    earStyle: 'round',
    tail: 'none',
    bounce: 0.8,
    eye: 0.85,
    rig: {
      parts: { head: 'capybara_part_head', torso: 'capybara_part_torso', arm: 'capybara_part_arm', leg: 'capybara_part_leg' },
      displayHeight: 300,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.22, 0.16], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 1.15, leg: 0, tail: 0, head: 0, torso: 0 },
        joints: { neck: [4, -0.74], shoulder: [2, -0.58], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  ostrich: {
    id: 'ostrich',
    body: 0xe8d3a8, belly: 0xf2e6c8, limb: 0xd8a878, accent: 0x2a2118, marking: 0x3a2c1a,
    bodyLen: 1.0, bodyHeight: 1.0, legLen: 1.4, legThick: 0.7, headSize: 1.0, neck: 1.6,
    earStyle: 'round', tail: 'none', bounce: 1.0, eye: 1.3,
    rig: {
      parts: { head: 'ostrich_part_head', torso: 'ostrich_part_torso', arm: 'ostrich_part_arm', leg: 'ostrich_part_leg' },
      displayHeight: 330,
      geometry: {
        origin: { torso: [0.5, 0.85], head: [0.5, 0.95], arm: [0.45, 0.25], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 0.3, leg: 0, tail: 0, head: 0, torso: 0 },
        joints: { neck: [0, -0.6], shoulder: [-14, -0.42], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  kangaroo: {
    id: 'kangaroo',
    body: 0xb56a3e, belly: 0xd49a6e, limb: 0xa55e34, accent: 0x4a2c18, marking: 0x6e4226,
    bodyLen: 1.05, bodyHeight: 1.0, legLen: 1.1, legThick: 1.05, headSize: 0.95, neck: 0.9,
    earStyle: 'pointy', tail: 'long', bounce: 1.05, eye: 1.0,
    rig: {
      parts: { head: 'kangaroo_part_head', torso: 'kangaroo_part_torso', arm: 'kangaroo_part_arm', leg: 'kangaroo_part_leg', tail: 'kangaroo_part_tail' },
      displayHeight: 310,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.22, 0.16], leg: [0.5, 0.05], tail: [0.85, 0.3] },
        rest: { arm: 1.15, leg: 0, tail: 0.4, head: 0, torso: 0 },
        joints: { neck: [8, -0.82], shoulder: [6, -0.66], hip: [0, -0.04], tail: [-50, -0.3] },
      },
    },
  },
  hare: {
    id: 'hare',
    body: 0xc99a6a, belly: 0xe8d3b4, limb: 0xbf8f5e, accent: 0x4a3526, marking: 0x6e4a2e,
    bodyLen: 0.92, bodyHeight: 0.95, legLen: 1.25, legThick: 0.75, headSize: 1.0, neck: 1.0,
    earStyle: 'pointy', tail: 'none', bounce: 1.05, eye: 1.15,
    rig: {
      parts: { head: 'hare_part_head', torso: 'hare_part_torso', arm: 'hare_part_arm', leg: 'hare_part_leg' },
      displayHeight: 320,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.92], arm: [0.5, 0.08], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 0, leg: 0, tail: 0, head: 0, torso: 0 },
        joints: { neck: [6, -0.88], shoulder: [-2, -0.66], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  elephant: {
    id: 'elephant',
    body: 0x9aa0a8, belly: 0xb4bac2, limb: 0x868c94, accent: 0x4a4e54, marking: 0x6e7278,
    bodyLen: 1.35, bodyHeight: 1.15, legLen: 0.9, legThick: 1.4, headSize: 1.15, neck: 0.6,
    earStyle: 'round', tail: 'none', bounce: 0.8, eye: 0.9,
    rig: {
      parts: { head: 'elephant_part_head', torso: 'elephant_part_torso', arm: 'elephant_part_arm', leg: 'elephant_part_leg' },
      displayHeight: 320,
      geometry: {
        origin: { torso: [0.5, 0.9], head: [0.5, 0.9], arm: [0.5, 0.1], leg: [0.5, 0.05], tail: [0.5, 0.5] },
        rest: { arm: 0.1, leg: 0, tail: 0, head: 0, torso: 0 },
        joints: { neck: [6, -0.8], shoulder: [8, -0.62], hip: [0, -0.04], tail: [-40, -0.4] },
      },
    },
  },
  frog: {
    id: 'frog',
    body: 0x6abf4a, belly: 0xbfe0a0, limb: 0x57a83c, accent: 0x2a4a1a, marking: 0x3e7a2a,
    bodyLen: 0.9, bodyHeight: 0.95, legLen: 1.3, legThick: 0.7, headSize: 1.1, neck: 0.5,
    earStyle: 'round', tail: 'none', bounce: 1.15, eye: 1.4,
    // Whole-body sprite for now (green-on-mint defeats the chroma key; articulated rig next).
    sprite: { key: 'frog_full', displayHeight: 300 },
  },
  penguin: {
    id: 'penguin', body: 0x2a2a30, belly: 0xf4f7ff, limb: 0x3a3a42, accent: 0xff9a3c, marking: 0x1a1a1e,
    bodyLen: 0.95, bodyHeight: 1.05, legLen: 0.7, legThick: 1.0, headSize: 1.0, neck: 0.5,
    earStyle: 'round', tail: 'none', bounce: 0.85, eye: 1.2,
    sprite: { key: 'penguin_full', displayHeight: 285 },
  },
  bear: {
    id: 'bear', body: 0x8a5a36, belly: 0xb98a5e, limb: 0x744a2c, accent: 0x3a261a, marking: 0x5c3c24,
    bodyLen: 1.3, bodyHeight: 1.15, legLen: 0.95, legThick: 1.35, headSize: 1.05, neck: 0.6,
    earStyle: 'round', tail: 'none', bounce: 0.85, eye: 1.0,
    sprite: { key: 'bear_full', displayHeight: 305 },
  },
  lemur: {
    id: 'lemur', body: 0x9aa0a6, belly: 0xe8ecf2, limb: 0x868c92, accent: 0x2a2a30, marking: 0x4a4a52,
    bodyLen: 0.92, bodyHeight: 0.95, legLen: 1.15, legThick: 0.75, headSize: 1.05, neck: 0.8,
    earStyle: 'round', tail: 'long', bounce: 1.1, eye: 1.5,
    sprite: { key: 'lemur_full', displayHeight: 300 },
  },
  octopus: {
    id: 'octopus', body: 0x8a4ad6, belly: 0xb083e6, limb: 0x6e34b8, accent: 0x3a1a66, marking: 0x5a2a96,
    bodyLen: 1.0, bodyHeight: 1.0, legLen: 0.9, legThick: 1.0, headSize: 1.3, neck: 0.4,
    earStyle: 'round', tail: 'none', bounce: 1.0, eye: 1.4,
    sprite: { key: 'octopus_full', displayHeight: 300 },
  },
  wolf: {
    id: 'wolf', body: 0x9a9ea4, belly: 0xd8dce2, limb: 0x82868c, accent: 0x2a2c32, marking: 0x4a4c54,
    bodyLen: 1.0, bodyHeight: 1.0, legLen: 1.1, legThick: 0.85, headSize: 1.05, neck: 0.85,
    earStyle: 'pointy', tail: 'long', bounce: 1.0, eye: 1.3,
    sprite: { key: 'wolf_full', displayHeight: 300 },
  },
};

export function getSkin(id: string): SkinDef {
  const skin = SKINS[id];
  if (!skin) {
    throw new Error(`Unknown skin "${id}"`);
  }
  return skin;
}
